import type {
  ContextState,
  DateFilterSelectedValues,
  Filter,
  PivotableToolConfig,
  RegularFilter,
  ToolConfigPivot,
} from '../../models'
import type { ToolConfigProvider } from '../../providers/interfaces/tool-config.provider'
import type { FiltersComposable } from './types'
// @ts-strict-ignore
import { computed, getCurrentInstance, onMounted, ref } from 'vue'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { FilterTypeEnum } from '../../enums/filter-type.enum'
import { CONTEXT_CONSTS } from '../../store/context/context.consts'
import { FiltersUtils } from '../../utils/filters.utils'
import { useFeature } from '../use-feature'
import { mapGetters } from '../utils'

export const useFilters = (): FiltersComposable => {
  const ctx = getCurrentInstance()

  const { featureId, section } = useFeature()

  const { context } = mapGetters<ContextState>(ctx.proxy.$store, CONTEXT_CONSTS.moduleName, {
    context: CONTEXT_CONSTS.getters.context,
  })

  const availableFiltersFilteredByTool = ref<Record<string, Filter>>({})

  const filters = computed<Record<string, Filter>>(() => {
    return context.value.filters
  })

  const availableFilters = computed<Record<string, Filter>>(() => {
    if (!section.value && !featureId.value) {
      return filters.value
    }
    return FiltersUtils.filterBySection(filters.value, section.value, featureId.value)
  })

  function isFilterVisible(filterConfig: RegularFilter['config']): boolean {
    return filterConfig.values.length > 1
  }

  const selectedDates = computed(() => {
    return filters.value[FilterTypeEnum.DATE]?.selectedValues as DateFilterSelectedValues
  })

  const getPivotsFromToolConfigProvider = async (): Promise<PivotableToolConfig['pivots']> => {
    const getToolConfig: ToolConfigProvider = async (...args) => {
      try {
        return await CONTAINER.get<ToolConfigProvider>(TYPES.ToolConfigProvider)(...args)
      } catch (error) {
        return { pivots: [] }
      }
    }
    const { pivots: pivotsFromTheFeature } = await getToolConfig(featureId.value)

    if (pivotsFromTheFeature?.length) {
      return pivotsFromTheFeature
    }

    const { pivots: pivotsFromTheSection } = await getToolConfig(section.value)
    return pivotsFromTheSection ?? []
  }

  const getFiltersByTool = (toolConfigPivots: ToolConfigPivot[]): { [p: string]: Filter } => {
    const filtersAccepted = toolConfigPivots.map(filter => filter.name)
    return FiltersUtils.filterByIds(filters.value, filtersAccepted)
  }

  const getAvailableFiltersFilteredByTool = async (): Promise<void> => {
    const pivotsConfigured = await getPivotsFromToolConfigProvider()

    if (pivotsConfigured?.length) {
      availableFiltersFilteredByTool.value = FiltersUtils.filterBySection(
        getFiltersByTool(pivotsConfigured),
        section.value,
        featureId.value,
      )
    } else {
      availableFiltersFilteredByTool.value = availableFilters.value
    }
  }

  onMounted(async () => {
    await getAvailableFiltersFilteredByTool()
  })

  return {
    filters,
    availableFilters,
    availableFiltersFilteredByTool,
    isFilterVisible,
    selectedDates,
  }
}

export * from './types'
